import * as _ from "lodash";

import { BarData, ChartSeriesType } from "@ctra/api";
import { i18nTranslate, Enterprise as Content } from "@ctra/i18n";

import { BarChartProcessor } from "./Core";
import { ChartContextType } from "../../../../providers";

const {
  kpi: { displayName }
} = Content;

export class MultiEntityBarChartProcessor extends BarChartProcessor {
  public identity = "MultiEntityBarChartProcessor";

  /**
   * Tell if the bar chart may be grouped
   * @param {ChartContextType<BarData>["values"]["meta"]} meta
   */
  mayGroup(meta: ChartContextType<BarData>["values"]["meta"]) {
    /**
     * Single farm charts will not get the farm ID appended to the series field
     * @type {boolean}
     */
    const isMultiFarm = _.size(_.uniq(_.compact(_.flatMap(meta, "farmIds")))) > 1;

    /**
     * Tell if the overlay still uses a single series type
     * @type {boolean}
     */
    const singleSeriesType = _.size(_.uniq(_.compact(_.map(meta, "seriesType")))) === 1;

    return isMultiFarm && singleSeriesType;
  }

  /**
   * Tell if the chart is a multi-farm chart
   * @param {ChartContextType<BarData>["values"]["meta"]} meta
   * @returns {boolean}
   */
  isMultiFarm(meta: ChartContextType<BarData>["values"]["meta"]): boolean {
    return _.size(_.uniq(_.compact(_.flatMap(meta, "farmIds")))) > 1;
  }

  /**
   * Make new series object with unique keys
   * @param {ChartContextType<BarData>["values"]["meta"]} meta
   * @param {ChartContextType<BarData>["values"]["series"]} series
   * @returns {{[p: string]: any} | {[p: string]: {}}}
   */
  getSeries(
    meta: ChartContextType<BarData>["values"]["meta"],
    series: ChartContextType<BarData>["values"]["series"]
  ) {
    /**
     * Tell if the overlay still uses a single series type
     * @type {boolean}
     */
    const isSingleSeriesType = this.mayGroup(meta);

    if (isSingleSeriesType) {
      return _.merge({}, ..._.map(meta, "series.keys"));
    }

    const isMultiFarm = this.isMultiFarm(meta);

    /**
     * Collect all series and make them unique per chart
     * @type {{[p: string]: {}}}
     */
    return _.reduce(
      _.mapValues(meta, "series.keys"),
      (acc, series, chartReference) => {
        const [variantID] = chartReference.split("-");
        const seriesType: ChartSeriesType = _.get(meta, [chartReference, "seriesType"]);

        /**
         * Do not make a `variantID.farmID` series field when there is only one farm.
         * For other series, keep it up.
         * @type {boolean}
         */
        const omitSeriesType = !isMultiFarm && seriesType === ChartSeriesType.farm;

        /**
         * Make new series keys for chart combinations
         * @type {Dictionary<{}>}
         */
        const updatedKey = _.mapKeys(series, (v, key) => {
          return omitSeriesType ? variantID : _.join([variantID, key], ".");
        });

        const isFarmSeries = _.get(meta, [chartReference, "seriesType"]) === ChartSeriesType.farm;

        if (isFarmSeries) {
          const title = _.get(meta, [chartReference, "title"]);

          return {
            ...acc,
            ..._.mapValues(updatedKey, (v) =>
              i18nTranslate(displayName(title), {
                case: null,
                variant: null,
                makeDefaultValue: true
              })
            )
          };
        } else {
          return {
            ...acc,
            ...updatedKey
          };
        }
      },
      {}
    );
  }

  /**
   * Update the data so the series field is unique for each line
   * @param {ChartContextType<BarData>["values"]["data"]} data
   * @param {ChartContextType<BarData>["values"]["meta"]} meta
   * @param {ChartContextType<BarData>["values"]["series"]} series
   * @returns {ChartContextType<BarData>["values"]["data"]}
   */
  getConsolidatedData(
    data: ChartContextType<BarData>["values"]["data"],
    meta: ChartContextType<BarData>["values"]["meta"],
    series: ChartContextType<BarData>["values"]["series"]
  ): ChartContextType<BarData>["values"]["data"] {
    /**
     * Tell if the overlay still uses a single series type
     * @type {boolean}
     */
    const isSingleSeriesType = this.mayGroup(meta);

    /**
     * Filter out any unnecessary data
     * @type {Record<ChartEntity["id"], Optional<Bar>>}
     */
    const filteredData = this.getFilteredRawData(data, meta, series);

    return _.mapValues(filteredData, (barCollection, chartReference) => {
      const seriesType: ChartSeriesType = _.get(meta, [chartReference, "seriesType"]);
      const isMultiFarm = this.isMultiFarm(meta);

      /**
       * Do not make a `variantID.farmID` series field when there is only one farm.
       * For other series, keep it up.
       * @type {boolean}
       */
      const omitSeriesType = !isMultiFarm && seriesType === ChartSeriesType.farm;

      return _.map(barCollection, ({ seriesField, y, ...rest }) => {
        const [variantID] = chartReference.split("-");
        const fieldValue = omitSeriesType ? variantID : _.join([variantID, seriesField], ".");

        return {
          ...rest,
          y: isSingleSeriesType ? (isMultiFarm ? variantID : y) : fieldValue,
          seriesField: isSingleSeriesType ? (isMultiFarm ? y : variantID) : fieldValue
        };
      });
    });
  }
}
